
/**
 * Authentication Configuration
 * Centralizes credential management to avoid hardcoding in multiple files.
 *
 * Round 8 Part A: no hardcoded credential. Identity comes from a signed
 * session token issued by POST /api/auth/login or the Google OAuth
 * callback, stored here (not a shared Basic Auth secret + client-echoed
 * X-User-Email, which the backend no longer accepts at all).
 *
 * Round 10 session-safety: a monotonic session generation invalidates
 * in-flight background profile refreshes across App + Settings logout
 * without storing user PII in module state.
 */

const SESSION_TOKEN_KEY = 'sessionToken';

const TEMPLATE_MANAGER_ROLES = new Set(['creator', 'admin', 'super_admin']);
const ADMIN_ROLES = new Set(['admin', 'super_admin']);

/** In-memory only — never persisted; never holds profile/PII. */
let sessionGeneration = 0;

export const getSessionGeneration = () => sessionGeneration;

export const bumpSessionGeneration = () => {
    sessionGeneration += 1;
    return sessionGeneration;
};

export const isSessionGenerationCurrent = (capturedGeneration) => {
    return capturedGeneration === sessionGeneration;
};

/**
 * Shared logout invalidation authority used by App and Settings.
 * Bumps generation first so outstanding profile refreshes become stale,
 * then clears local session fields. Analytics reset is caller-supplied
 * so PostHog availability never gates session clearing.
 */
export const invalidateLocalSession = ({ resetAnalytics } = {}) => {
    bumpSessionGeneration();
    clearSession();
    if (typeof resetAnalytics === 'function') {
        try {
            resetAnalytics();
        } catch {
            /* analytics must never block logout */
        }
    }
    return sessionGeneration;
};

export const setSessionToken = (token) => {
    if (token) {
        localStorage.setItem(SESSION_TOKEN_KEY, token);
    }
};

export const getSessionToken = () => localStorage.getItem(SESSION_TOKEN_KEY) || '';

export const clearSession = () => {
    localStorage.removeItem(SESSION_TOKEN_KEY);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('userPicture');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
};

/**
 * Returns the HTTP Authorization header value for API calls.
 * @returns {string} - "Bearer <session_token>"
 */
export const getAuthHeader = () => {
    return `Bearer ${getSessionToken()}`;
};

/**
 * Returns the authenticated user's id, cached at login.
 * @returns {string}
 */
export const getUserId = () => {
    return localStorage.getItem('userId') || '';
};

export const getUserRole = () => {
    return (localStorage.getItem('userRole') || '').trim().toLowerCase();
};

export const normalizeRole = (role) => {
    if (role == null) return '';
    return String(role).trim().toLowerCase();
};

/**
 * Authoritative frontend admin predicate (UX only — backend still enforces).
 * Allowed: admin, super_admin. Denied: creator, user, viewer, missing/unknown.
 */
export const isAdminRole = (role) => {
    return ADMIN_ROLES.has(normalizeRole(role));
};

export const canAccessAdminPanel = () => {
    return isAdminRole(getUserRole());
};

export const canManageTemplates = () => {
    return TEMPLATE_MANAGER_ROLES.has(getUserRole());
};

/** Test-only helper to reset generation between unit cases. */
export const __resetSessionGenerationForTests = () => {
    sessionGeneration = 0;
};
